import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import UnauthorizedAccessModal from "../components/Shared/UnauthorizedAccessModal/UnauthorizedAccessModal";
import useNavigateBack from "./useNavigateBack";
import useSessionExpired from "./useSessionExpired";

/**
 * Custom React hook for securely accessing protected API resources
 *
 * This hook provides:
 * - Automatic data fetching with loading states
 * - Error handling for API requests
 * - Unauthorized access detection (403 responses)
 * - An integrated modal component for unauthorized access
 * - Automatic redirection when access is denied
 *
 * @param {Function} apiFunction - The API function to call (e.g., getAllContainerDataByContainerId)
 * @param {any} params - Parameters to pass to the API function (e.g., containerId)
 * @param {string} resourceType - Type of resource being accessed (e.g., 'container', 'location')
 *                              - Used for display in the unauthorized access modal
 * @param {string} fallbackPath - Path to redirect to when access is denied (defaults to "/dashboard")
 *
 * @returns {Object} Object containing:
 *   - data: The fetched resource data
 *   - isLoading: Boolean indicating if the request is in progress
 *   - error: Error message if the request failed (not for auth errors)
 *   - UnauthorizedAccessModal: Ready-to-use modal component for unauthorized access
 */

const useProtectedResource = (
  apiFunction,
  params,
  resourceType,
  fallbackPath = "/dashboard",
  customOnDismiss = null
) => {
  // States
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUnauthorizedModal, setShowUnauthorizedModal] = useState(false);

  // Navigation and auth hooks
  const navigate = useNavigate();
  const location = useLocation();
  const navigateBack = useNavigateBack(fallbackPath);
  const { setSessionExpired } = useSessionExpired();

  useEffect(() => {
    // Track component mount state to prevent state updates after unmount
    let isMounted = true;
    const fetchData = async () => {
      // Skip API call if no parameters provided
      if (!params) {
        setIsLoading(false);
        return;
      }

      try {
        // Start loading state and make the API call
        setIsLoading(true);
        const result = await apiFunction(params);

        // Only update state if component is still mounted
        if (isMounted) {
          setData(result);
          setIsLoading(false);
        }
      } catch (error) {
        // Prevent state updates if component unmounted
        if (!isMounted) return;

        console.log("API Error:", error);

        // Session expired (401)
        if (error.status === 401) {
          setSessionExpired(true);
          return;
        }

        // Update the error check to be more comprehensive
        if (
          error.isAuthError ||
          error.status === 403 ||
          (error.response && error.response.status === 403)
        ) {
          console.log("Access denied, showing modal"); // Add this for debugging
          setShowUnauthorizedModal(true);
        } else {
          // Set error state for other errors
          setError(error.message || "An unexpected error occurred");
        }
      } finally {
        // Ensure loading state is reset even if there's an error
        if (isMounted) setIsLoading(false);
      }
    };

    // Execute the data fetching
    fetchData();

    // Cleanup function to prevent memory leaks and state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [apiFunction, params, setSessionExpired]);

  /**
   * Handler for when user dismisses the unauthorized access modal
   * Hides the modal and navigates to the fallback path
   */
  const handleUnauthorizedDismiss = () => {
    setShowUnauthorizedModal(false);

    if (customOnDismiss) {
      customOnDismiss();
    } else {
      navigateBack();
    }
  };

  // Add inside your useEffect
  useEffect(() => {
    //console.log("Modal state:", { showUnauthorizedModal, error });
  }, [showUnauthorizedModal, error]);

  // Return values and components for the consumer to use
  return {
    data,
    isLoading: isLoading && !showUnauthorizedModal,
    error,
    UnauthorizedModal: (
      <UnauthorizedAccessModal
        show={showUnauthorizedModal}
        onDismiss={handleUnauthorizedDismiss}
        resourceType={resourceType}
      />
    ),
  };
};

export default useProtectedResource;
